/**
 * Auth/session store (Zustand). The JWT lives in the API client's in-memory
 * variable ONLY (never web storage) — this store holds just the non-sensitive
 * session status, user name and roles for the UI. On a 401 from any
 * authenticated call, the client invokes the registered handler, which clears
 * the session and flips status to `expired` so the app redirects to Login.
 * There is no refresh-token endpoint in the contract (the JWT is a Dynizer
 * pass-through), so recovery is re-authentication.
 */
import { create } from 'zustand';
import { apiRequest, setAuthToken, setUnauthorizedHandler, ApiError } from '../api';

export type AuthStatus = 'anonymous' | 'authenticating' | 'authenticated' | 'expired';

export interface AuthState {
  status: AuthStatus;
  user: string | null;
  roles: string[];
  error: string | null;
  login: (user: string, password: string) => Promise<void>;
  logout: () => void;
  /** Called by the 401 handler; drops the session without a user action. */
  expire: () => void;
}

export const useAuthStore = create<AuthState>((set) => ({
  status: 'anonymous',
  user: null,
  roles: [],
  error: null,

  login: async (user, password) => {
    set({ status: 'authenticating', error: null });
    try {
      const res = await apiRequest('/auth/login', 'post', {
        body: { user, password },
        skipAuthRedirect: true,
      });
      if (!res?.jwt) {
        throw new Error('Login response did not include a token.');
      }
      setAuthToken(res.jwt);
      set({ status: 'authenticated', user, roles: res.roles ?? [], error: null });
    } catch (err) {
      setAuthToken(null);
      const message =
        err instanceof ApiError && err.status === 401
          ? 'Incorrect username or password.'
          : err instanceof Error
            ? err.message
            : 'Login failed.';
      set({ status: 'anonymous', user: null, roles: [], error: message });
      throw err;
    }
  },

  logout: () => {
    setAuthToken(null);
    set({ status: 'anonymous', user: null, roles: [], error: null });
  },

  expire: () => {
    setAuthToken(null);
    set({ status: 'expired', user: null, roles: [], error: 'Your session expired. Please sign in again.' });
  },
}));

/**
 * Bridge the client's 401 handler to the store. Call once at app startup.
 * Returns the unsubscribe function.
 */
export function installAuthInterceptor(): () => void {
  setUnauthorizedHandler(() => {
    // Only meaningful if we currently believe we are authenticated.
    const { status, expire } = useAuthStore.getState();
    if (status === 'authenticated' || status === 'authenticating') {
      expire();
    }
  });
  return () => setUnauthorizedHandler(null);
}
